import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';
import { useCurrency } from '../../hooks/useCurrency';
import { useLanguage } from '../../hooks/useLanguage';
import { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { getDisplayPrice, getSecondaryPrice } from '../../lib/currency';
import WindowsSeoJsonLd from './components/SeoJsonLd';

export default function WindowsPage() {
  const { addItem } = useCart();
  const { currency } = useCurrency();
  const { t } = useLanguage();
  const [addedId, setAddedId] = useState<string | null>(null);
  const { containerRef, getAnimationClass, getAnimationStyle } = useScrollAnimation();

  const windowsLicenses = [
    {
      id: 'win11home',
      name: 'Licencia Windows 11 HOME Original',
      price: 79900,
      oldPrice: 99900,
      discount: '-20%',
      image:
        'https://public.readdy.ai/ai/img_res/de0d0157b6105917c58a8d5bf4d73da7.jpg',
      badge: t('product.offer'),
      description: t('windows.home_desc'),
      type: 'Licencia Digital - 1 PC',
      salesCount: 156,
    },
    {
      id: 'win10pro',
      name: 'Licencia Windows 10 PRO Original',
      price: 55385,
      oldPrice: 119900,
      discount: '-54%',
      image:
        'https://readdy.ai/api/search-image?query=windows%2010%20professional%20operating%20system%20software%20elegant%20product%20box%20packaging%20modern%20blue%20gradient%20design%20with%20windows%20logo%20clean%20white%20background%20professional%20studio%20lighting%20premium%20quality%20product%20photography%20sharp%20details&width=250&height=250&seq=win10pro2&orientation=squarish',
      badge: t('product.offer'),
      description: t('windows.10pro_desc'),
      type: 'Licencia Digital - 1 PC',
      salesCount: 287,
    },
    {
      id: 'win11pro',
      name: 'Licencia Windows 11 PRO Original',
      price: 65963,
      oldPrice: 129900,
      discount: '-49%',
      image:
        'https://readdy.ai/api/search-image?query=windows%2011%20professional%20operating%20system%20software%20sleek%20modern%20product%20box%20packaging%20vibrant%20blue%20gradient%20design%20with%20new%20windows%2011%20logo%20clean%20white%20background%20professional%20studio%20lighting%20premium%20quality%20product%20photography%20sharp%20details&width=250&height=250&seq=win11pro2&orientation=squarish',
      badge: t('product.offer'),
      description: t('windows.11pro_desc'),
      type: 'Licencia Digital - 1 PC',
      salesCount: 340,
    },
  ];

  const handleAddToCart = (product: typeof windowsLicenses[0]) => {
    try {
      addItem({
        id: product.id,
        name: product.name,
        type: product.type,
        price: product.price,
        oldPrice: product.oldPrice,
        image: product.image,
      });
      setAddedId(product.id);
      setTimeout(() => setAddedId(null), 1500);
    } catch (error) {
      console.error('Error adding product to cart:', error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <WindowsSeoJsonLd />
      <Header />

      {/* Hero */}
      <section className="relative h-[200px] sm:h-[300px] bg-gradient-to-r from-blue-600 to-cyan-500 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20windows%20operating%20system%20interface%20elegant%20blue%20gradient%20background%20with%20floating%20transparent%20window%20panels%20clean%20minimalist%20desktop%20environment%20professional%20technology%20atmosphere%20soft%20ambient%20lighting%20digital%20workspace%20concept%20futuristic%20design%20high%20quality&width=1920&height=400&seq=winhero2&orientation=landscape"
            alt="Windows Background"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative container mx-auto px-4 h-full flex items-center">
          <div className="text-white">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-4">
              {t('windows.title')}
            </h1>
            <p className="text-sm sm:text-lg md:text-xl max-w-2xl leading-relaxed">
              {t('windows.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Products */}
      <main className="flex-1 py-8 sm:py-16">
        <div className="container mx-auto px-4">
          <div
            ref={containerRef}
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6"
            data-product-shop
          >
            {windowsLicenses.map((product, index) => {
              const display = getDisplayPrice(product.price, product.oldPrice, currency);
              const secondary = getSecondaryPrice(product.price, product.oldPrice, currency);
              return (
                <div
                  key={product.id}
                  data-anim-index={index}
                  style={getAnimationStyle(index)}
                  className={`bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-500 ease-out group cursor-pointer ${getAnimationClass(
                    index
                  )}`}
                >
                  <div className="relative">
                    {product.badge && (
                      <span className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-red-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded whitespace-nowrap">
                        {product.badge}
                      </span>
                    )}
                    {product.discount && (
                      <span className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-red-500 text-white text-[10px] sm:text-xs font-semibold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded whitespace-nowrap">
                        {product.discount}
                      </span>
                    )}
                    <div className="w-full h-32 sm:h-48 bg-gray-50 flex items-center justify-center p-3 sm:p-4">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                      />
                    </div>
                  </div>
                  <div className="p-3 sm:p-4">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-900 mb-1 sm:mb-2 line-clamp-2 min-h-[32px] sm:h-10">
                      {product.name}
                    </h3>
                    <p className="text-[10px] sm:text-xs text-gray-600 mb-2 sm:mb-3 line-clamp-2 hidden sm:block">
                      {product.description}
                    </p>
                    <div className="flex items-center gap-1 mb-2 sm:mb-3">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-yellow-400 text-[10px] sm:text-sm"></i>
                      ))}
                      <span className="text-[9px] sm:text-xs text-gray-500 ml-0.5">(5.0)</span>
                    </div>
                    <div className="flex items-center gap-1 mb-1.5 sm:mb-2">
                      <i className="ri-shopping-bag-line text-[10px] sm:text-xs text-orange-500"></i>
                      <span className="text-[10px] sm:text-xs text-gray-500">{product.salesCount.toLocaleString('es-CO')} vendidos</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
                      <span className="text-base sm:text-lg font-bold text-gray-900">
                        {display.main}
                      </span>
                      {display.oldMain && (
                        <span className="text-[10px] sm:text-sm text-gray-500 line-through">
                          {display.oldMain}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 mt-0.5">
                      <p className="text-[10px] sm:text-xs text-emerald-600 font-medium">
                        ≈ {secondary.current}
                      </p>
                      {secondary.old && (
                        <p className="text-[10px] sm:text-xs text-gray-400 line-through hidden sm:block">
                          {secondary.old}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleAddToCart(product)}
                      className={`w-full mt-3 sm:mt-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors whitespace-nowrap cursor-pointer ${
                        addedId === product.id
                          ? 'bg-green-500 text-white'
                          : 'bg-[#0078D4] text-white hover:bg-[#006abc]'
                      }`}
                    >
                      {addedId === product.id ? (
                        <>
                          <i className="ri-check-line mr-1"></i>
                          {t('product.added')}
                        </>
                      ) : (
                        <>
                          <i className="ri-shopping-cart-line mr-1 sm:hidden"></i>
                          <span className="hidden sm:inline">{t('product.add_to_cart')}</span>
                          <span className="sm:hidden">{t('product.add')}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
